import React, { useContext, VFC } from 'react';

import ArticleBlock from '/components/ArticleBlock';
import { ProductColumn } from '/components/BlockColumns/ProductColumn';
import {
  BlockProps,
  ColumnContainerJson,
  Present,
  ProductContainerJson,
} from '/types';
import { PresentModeContext } from '/utils/contexts';
import { contentFilter } from '/utils/functions';

const isAnimationComplete = (
  presentMode: Present,
  animationStep: number,
  columnCount: number,
  animationCount: number,
): boolean => {
  if (presentMode === Present.Keynote) {
    return animationStep >= columnCount + animationCount;
  }
  if (presentMode === Present.Full) {
    return animationStep > animationCount;
  }
  return true;
};

const isProductColumn = (
  column: ColumnContainerJson,
): column is ProductContainerJson =>
  'elements' in column &&
  'image' in column.elements &&
  'title' in column.elements;

const BlockProductWallAnalyst: VFC<BlockProps> = ({
  animationEnabled,
  animationStep,
  setAnimationComplete,
  blockContent,
  presentOption,
  ...props
}) => {
  const presentMode = useContext(PresentModeContext);
  const columnData = blockContent?.columns?.list || [];
  const contextContent = contentFilter(
    blockContent.context,
    presentMode,
    presentOption,
  );

  const descriptions: string[] = [];
  const hide: boolean[] = [];
  const expand: boolean[] = [];
  let animationCount = contextContent ? 1 : 0;

  columnData.forEach((column: ColumnContainerJson, columnNumber: number) => {
    const desc =
      column.name === 'product'
        ? contentFilter(column.elements.content, presentMode, presentOption)
        : '';

    // Push into bool array indicating if current column is to be hidden
    hide.push(
      presentMode !== Present.Full &&
        animationEnabled &&
        animationStep <= columnNumber + animationCount,
    );
    // Push into bool array indicating if description is to be expanded
    if (desc) {
      if (presentMode === Present.Keynote) {
        expand.push(animationStep > columnNumber + animationCount + 1);
        animationCount = animationCount + 1;
      } else if (presentMode === Present.Full) {
        expand.push(animationStep > animationCount);
      }
    } else {
      expand.push(false);
    }
    descriptions.push(desc);
  });

  const animationComplete = isAnimationComplete(
    presentMode,
    animationStep,
    columnData.length,
    animationCount,
  );
  setAnimationComplete(animationComplete);

  return (
    <ArticleBlock
      {...props}
      contextContent={contextContent}
      blockContent={blockContent}
      hideContext={animationEnabled && !animationStep}
      hideFooter={animationEnabled && !animationComplete}
      titleCentered
      offset={columnData.length === 2 ? 3 : 1}
      columns={columnData.map(
        (column: ColumnContainerJson, columnNumber: number) => (
          <>
            {!column.hidden && isProductColumn(column) && (
              <ProductColumn
                elements={column.elements}
                description={descriptions[columnNumber]}
                expand={expand[columnNumber]}
                hide={hide[columnNumber]}
              />
            )}
          </>
        ),
      )}
    />
  );
};

BlockProductWallAnalyst.displayName = 'BlockProductWallAnalyst';
export default BlockProductWallAnalyst;
